import React, { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Plus, Loader2, AlertTriangle } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { QuotationDataTable } from './QuotationDataTable';
import { columns, editQuotationEvent, deleteQuotationEvent, convertQuotationEvent } from './columns';
import { listQuotations, deleteQuotation, convertQuotationToSalesOrder } from '@/app/services/quotationService';
import { QuotationResponse } from '@/app/types/quotation';
import { useOrganization } from '@/app/hooks/useOrganization';
import { PaginatedResponse } from '@/app/types/api';
import { SortingState } from '@tanstack/react-table';

interface ExtendedPaginatedResponse<T> extends PaginatedResponse<T> {
  page_count?: number;
  pageCount?: number;
  total_pages?: number;
  totalPages?: number;
}

const QuotationsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useOrganization();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State management
  const initialPage = parseInt(searchParams.get('page') || '0', 10);
  const initialSearchQuery = searchParams.get('search') || '';
  const initialSortField = searchParams.get('sort_by') || 'transaction_date';
  const initialSortDir = searchParams.get('sort_dir') || 'desc';

  const [search, setSearch] = useState<string>(initialSearchQuery);
  const [page, setPage] = useState<number>(initialPage);
  const [limit] = useState<number>(10);
  const [sorting, setSorting] = useState<SortingState>([
    { id: initialSortField, desc: initialSortDir === 'desc' },
  ]);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState<boolean>(false);
  const [isConvertAlertOpen, setIsConvertAlertOpen] = useState<boolean>(false);
  const [selectedQuotation, setSelectedQuotation] = useState<QuotationResponse | null>(null);

  // Sync URL search params with state
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 0) params.set('page', page.toString());
    if (search) params.set('search', search);
    const currentSort = sorting[0];
    if (currentSort) {
      params.set('sort_by', currentSort.id);
      params.set('sort_dir', currentSort.desc ? 'desc' : 'asc');
    }
    setSearchParams(params, { replace: true });
  }, [page, search, sorting, setSearchParams]);

  // Get sort parameters for API call
  const getSortParams = useCallback(() => {
    if (sorting.length === 0) return { sort_by: 'transaction_date', sort_dir: 'desc' as 'asc' | 'desc' };
    const sort = sorting[0];
    return { sort_by: sort.id, sort_dir: (sort.desc ? 'desc' : 'asc') as 'asc' | 'desc' };
  }, [sorting]);

  // Fetch quotations data
  const { data: quotationsData, isLoading, isError, error } = useQuery<ExtendedPaginatedResponse<QuotationResponse>>({
    queryKey: ['quotations', page, limit, search, sorting, currentOrganizationId],
    queryFn: () => {
      const { sort_by, sort_dir } = getSortParams();
      return listQuotations({
        skip: page * limit,
        limit,
        search_query: search || undefined,
        sort_by,
        sort_dir,
      });
    },
    enabled: !!currentOrganizationId,
    placeholderData: (prevData) => prevData,
  });

  const pageCount = quotationsData?.pageCount ?? quotationsData?.page_count ?? 
                    quotationsData?.totalPages ?? quotationsData?.total_pages ?? 
                    (quotationsData?.total ? Math.ceil(quotationsData.total / limit) : 0);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteQuotation(id),
    onSuccess: () => {
      toast.success('Quotation deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['quotations'] });
      setIsDeleteAlertOpen(false);
      setSelectedQuotation(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete quotation: ${error.message}`);
      setIsDeleteAlertOpen(false);
    },
  });

  // Convert to Sales Order mutation
  const convertMutation = useMutation({
    mutationFn: (id: number) => convertQuotationToSalesOrder(id),
    onSuccess: (salesOrder) => {
      toast.success('Quotation converted to Sales Order successfully');
      queryClient.invalidateQueries({ queryKey: ['quotations'] });
      setIsConvertAlertOpen(false);
      setSelectedQuotation(null);
      // Navigate to the sales order detail page
      navigate(`/sales-orders/detail/${salesOrder.id}`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to convert quotation: ${error.message}`);
      setIsConvertAlertOpen(false);
    },
  });

  // Event handlers for custom events from columns
  useEffect(() => {
    const handleEditQuotation = (event: CustomEvent<{ quotation: QuotationResponse }>): void => {
      navigate(`/quotations/detail/${event.detail.quotation.id}`);
    };

    const handleDeleteQuotation = (event: CustomEvent<{ quotation: QuotationResponse }>): void => {
      setSelectedQuotation(event.detail.quotation);
      setIsDeleteAlertOpen(true);
    };

    const handleConvertQuotation = (event: CustomEvent<{ quotation: QuotationResponse }>): void => {
      setSelectedQuotation(event.detail.quotation);
      setIsConvertAlertOpen(true);
    };

    document.addEventListener(editQuotationEvent, handleEditQuotation as EventListener);
    document.addEventListener(deleteQuotationEvent, handleDeleteQuotation as EventListener);
    document.addEventListener(convertQuotationEvent, handleConvertQuotation as EventListener);

    return () => {
      document.removeEventListener(editQuotationEvent, handleEditQuotation as EventListener);
      document.removeEventListener(deleteQuotationEvent, handleDeleteQuotation as EventListener);
      document.removeEventListener(convertQuotationEvent, handleConvertQuotation as EventListener);
    };
  }, [navigate]);

  // Handlers for search, sort, page change
  const handleSearchChange = useCallback((value: string): void => {
    setSearch(value);
    setPage(0);
  }, []);

  const handleSortingChange = useCallback((newSorting: SortingState): void => {
    setSorting(newSorting);
    setPage(0);
  }, []);

  const handlePageChange = useCallback((newPage: number): void => {
    setPage(newPage);
  }, []);

  const handleDeleteConfirm = (): void => {
    if (selectedQuotation) {
      deleteMutation.mutate(selectedQuotation.id);
    }
  };

  const handleConvertConfirm = (): void => {
    if (selectedQuotation) {
      convertMutation.mutate(selectedQuotation.id);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight">Quotations</h1>
        <Button asChild>
          <Link to="/quotations/detail">
            <Plus className="h-4 w-4 mr-2" />
            New Quotation
          </Link>
        </Button>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading quotations: {(error as Error)?.message || 'Unknown error'}
          </AlertDescription>
        </Alert>
      )}

      <QuotationDataTable
        columns={columns}
        data={quotationsData?.items ?? []}
        isLoading={isLoading}
        pageCount={pageCount}
        page={page}
        setPage={handlePageChange}
        onSearchChange={handleSearchChange}
        searchValue={search}
        onSortingChange={handleSortingChange}
        sortingState={sorting}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this quotation?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The quotation will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground">
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Convert Confirmation Dialog */}
      <AlertDialog open={isConvertAlertOpen} onOpenChange={setIsConvertAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Convert Quotation to Sales Order?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new Sales Order based on this quotation and mark the quotation as converted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConvertConfirm}>
              {convertMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Convert
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default QuotationsPage; 